//src\components\portal\portal_components\design_settings.js
import React, { useState, useEffect } from 'react';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Container,
  TextField,
  Button,
  Grid,
  Snackbar,
  Chip
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PaletteIcon from '@mui/icons-material/Palette';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import portalSession from '../portal_services/portal_session'; 

const Designer = () => {
  const theme = useTheme();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [promptWarning, setPromptWarning] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    company_primary_color: '#009688',
    company_quick_q_pill: '',
    company_openchat_text: '',
    company_prompt: ''
  });

  // Inappropriate words list for filtering user input
  const inappropriateWords = [
    'porn', 'fuck', 'penis', 'vagina', 'nazi', 'cunt',
    'hore', 'slut', 'pussy', 'babe', 'adult', 'xx', 
    'nigger', 'bitch', 'kill', 'murder', 'suicide',
    'shit', 'poo', 'clit', 'fat', 'die',
    'por', 'fuc', 'peni', 'vagin', 'naz', 'cun',
    'hor', 'slu', 'puss', 'bab', 'adul', 'xx', 
    'nigger', 'bitch', 'kill', 'murder', 'suici',
    'shi', 'poo', 'clit', 'fat', 'die',
  ];

  // API configuration
  const BASE_URL = portalSession.getBaseURL();
  const CUSTOMER_ID = portalSession.getCustomerId();
  const CUSTOMER_TOKEN = portalSession.getCustomerToken();

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/get_customer/${CUSTOMER_ID}`, {
          method: 'GET',
          headers: {
            'X-Customer-ID': CUSTOMER_ID,
            'X-Customer-Token': CUSTOMER_TOKEN,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setCustomerData(data);
        
        // Set form data from API response
        setFormData({
          company_primary_color: data.company_primary_color || '#009688',
          company_quick_q_pill: data.company_quick_q_pill || '',
          company_openchat_text: data.company_openchat_text || '',
          company_prompt: data.company_prompt || ''
        });
        
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching customer data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, []);

  const checkForInappropriateContent = (text) => {
    const lowercaseText = text.toLowerCase();
    const foundWords = inappropriateWords.filter(word => 
      lowercaseText.includes(word.toLowerCase())
    );
    return foundWords;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Check for inappropriate content in company prompt
    if (field === 'company_prompt') {
      const inappropriateFound = checkForInappropriateContent(value);
      if (inappropriateFound.length > 0) {
        setPromptWarning(`Warning: Potentially inappropriate content detected. Please review your prompt.`);
      } else {
        setPromptWarning('');
      }
    }
  };

  const handleSave = async () => {
    // Final check for inappropriate content
    const inappropriateFound = checkForInappropriateContent(formData.company_prompt);
    if (inappropriateFound.length > 0) {
      setPromptWarning(`Cannot save: Inappropriate content detected. Please review your prompt.`);
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${BASE_URL}/edit_customer/${CUSTOMER_ID}`, {
        method: 'POST',
        headers: {
          'X-Customer-ID': CUSTOMER_ID,
          'X-Customer-Token': CUSTOMER_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSuccessMessage('Settings saved successfully!');
        setShowSaveConfirmation(true);
        // Update the customer data state
        setCustomerData(prev => ({ ...prev, ...formData }));
        
        // Hide confirmation after 2 seconds
        setTimeout(() => {
          setShowSaveConfirmation(false);
        }, 2000);
      } else {
        throw new Error(result.message || 'Failed to save settings');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  // Validation helpers
  const isQuickQPillValid = formData.company_quick_q_pill.length <= 20;
  const isOpenchatTextValid = formData.company_openchat_text.length <= 12;
  const isPromptValid = formData.company_prompt.split(' ').length <= 500;
  const hasInappropriateContent = checkForInappropriateContent(formData.company_prompt).length > 0;

  const canSave = isQuickQPillValid && isOpenchatTextValid && isPromptValid && !hasInappropriateContent;

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress color="primary" />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            Error loading designer settings: {error}
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Card 
          elevation={3}
          sx={{ 
            backgroundColor: 'white',
            border: '2px solid #009688',
            borderRadius: 2
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                color: '#009688',
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 3
              }}
            >
              <PaletteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Design Settings
            </Typography>
            
            <Grid container spacing={3}>
              {/* Primary Color */}
              <Grid item xs={12}>
                <Typography
                  variant="body" 
                  component="h4"
                  sx={{ 
                    color: '#textSecondary',
                    mb: 1
                  }}
                >
                  <ColorLensIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Primary Color
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '60px',
                      height: '40px',
                      border: '2px solid #ccc',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      '&:hover': {
                        border: '2px solid #009688'
                      }
                    }}
                  >
                    <input
                      type="color"
                      value={formData.company_primary_color}
                      onChange={(e) => handleInputChange('company_primary_color', e.target.value)}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        cursor: 'pointer',
                        backgroundColor: 'transparent'
                      }}
                    />
                  </Box>
                  <TextField
                    value={formData.company_primary_color}
                    onChange={(e) => handleInputChange('company_primary_color', e.target.value)}
                    placeholder="#009688"
                    size="small"
                    sx={{ width: '120px' }}
                  />
                </Box>
              </Grid>

              {/* Quick Q Pill */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Quick Q Pill Text"
                  value={formData.company_quick_q_pill}
                  onChange={(e) => handleInputChange('company_quick_q_pill', e.target.value)}
                  error={!isQuickQPillValid}
                  helperText={`${formData.company_quick_q_pill.length}/20 characters ${!isQuickQPillValid ? '(Too long!)' : ''}`}
                  inputProps={{ maxLength: 25 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#009688',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#009688',
                    },
                  }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Pre-populated common questions that show for users to click on
                </Typography>
              </Grid>

              {/* Open Chat Text */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Open Chat Text"
                  value={formData.company_openchat_text}
                  onChange={(e) => handleInputChange('company_openchat_text', e.target.value)}
                  error={!isOpenchatTextValid}
                  helperText={`${formData.company_openchat_text.length}/12 characters ${!isOpenchatTextValid ? '(Too long!)' : ''}`}
                  inputProps={{ maxLength: 15 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#009688',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#009688',
                    },
                  }}
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Text that shows in the button in the top right corner of the searchable app. Default is "Open Chat", but you can customize it to align with your brand.
                </Typography>
              </Grid>

              {/* Company Prompt */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Write Prompt here"
                  placeholder="E.g. ** Target Audience: ** ** Brand Tone & Slang: ** ** Key Information ** ** Information to AVOID ** ** Format: **"
                  value={formData.company_prompt}
                  onChange={(e) => handleInputChange('company_prompt', e.target.value)}
                  error={!isPromptValid || hasInappropriateContent}
                  helperText={`${formData.company_prompt.split(' ').filter(word => word.length > 0).length}/500 words ${!isPromptValid ? '(Too long!)' : ''}`}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#009688',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#009688',
                    },
                  }}
                />
                
                {/* Prompt Warning */}
                {promptWarning && (
                  <Alert 
                    severity="warning" 
                    sx={{ mt: 1 }}
                    icon={<WarningIcon />}
                  >
                    {promptWarning}
                  </Alert>
                )}
              </Grid>

              {/* Company Info Display and Prompt Instructions */}
              <Grid item xs={12}>
                {customerData && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f9ff', borderRadius: 1 }}>
                    {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      <strong>Company:</strong> {customerData.company_name} | 
                      <strong> URL:</strong> {customerData.company_URL}
                    </Typography> */}
                    
                    <Typography variant="body2" color="textSecondary">
                      <strong>Prompt Instructions:</strong> The prompt is for your organization's search tool on your website. 
                      Update the prompt to encourage a tone in line with your brand. If you are a sports organization, you might 
                      want to include key slang that your users may use. You may want to not answer certain questions, 
                      i.e., fees-related, due to the complexity or differing information available. You can add links to tools 
                      you may want to refer users to that are not on your website, e.g., a related organizational body might 
                      have a webpage relevant to your users. Including a target audience is useful - describe the types of 
                      users you get to your website, i.e., creative, young, professionals, academic, sporty, etc.
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSave}
                    disabled={!canSave || saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontWeight: 'bold',
                      backgroundColor: '#009688',
                      '&:hover': {
                        backgroundColor: '#00796b',
                      }
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                  
                  {/* Save Confirmation Tick */}
                  {showSaveConfirmation && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Saved!"
                      color="success"
                      variant="filled"
                      sx={{
                        fontWeight: 'bold',
                        animation: 'fadeIn 0.3s ease-in'
                      }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={() => setSuccessMessage('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSuccessMessage('')} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Add CSS for fade-in animation */}
        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.8); }
            to { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </Container>
    </ThemeProvider>
  );
};

export default Designer;